import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, Badge, Table } from 'react-bootstrap';
import { FaUpload, FaRocket, FaCheckCircle, FaExclamationTriangle, FaFileAlt, FaDownload, FaFilter } from 'react-icons/fa';
import Layout from './Layout';
import axios from 'axios';
import '../assets/css/SpProductCampaigns.css';

const SpProductCampaigns = () => {
    // API Configuration
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

    // Estados para archivos
    const [ownProductsFile, setOwnProductsFile] = useState(null);
    const [competitorFile, setCompetitorFile] = useState(null);
    const [negativeFile, setNegativeFile] = useState(null);

    // Estados de filtros
    const [filters, setFilters] = useState({
        by_reviews: false,
        less_reviews_than_us: true,
        by_price: false,
        higher_price_than_us: true,
        by_rating: false,
        less_rating_than_us: true
    });

    // Estados de UI
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [campaigns, setCampaigns] = useState([]);
    const [analysis, setAnalysis] = useState(null);
    const [creationComplete, setCreationComplete] = useState(false);
    const [campaignState, setCampaignState] = useState('PAUSED');

    // Bulk actions states
    const [bulkAction, setBulkAction] = useState('');
    const [bulkBudget, setBulkBudget] = useState('');
    const [bulkStartDate, setBulkStartDate] = useState('');
    const [bulkEndDate, setBulkEndDate] = useState('');

    // Manejar carga de archivos
    const handleOwnProductsFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.name.endsWith('.csv')) {
            setOwnProductsFile(file);
        }
    };

    const handleCompetitorFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.name.endsWith('.csv')) {
            setCompetitorFile(file);
        }
    };

    const handleNegativeFileChange = (e) => {
        const file = e.target.files[0];
        if (file && file.name.endsWith('.csv')) {
            setNegativeFile(file);
        }
    };

    // Manejar cambio de filtros
    const handleFilterChange = (filterName) => {
        setFilters(prev => ({
            ...prev,
            [filterName]: !prev[filterName]
        }));
    };

    // Generar campañas
    const handleGenerateCampaigns = async () => {
        if (!ownProductsFile || !competitorFile) {
            setError('Debes subir los archivos de Productos Propios y Competidores');
            return;
        }

        setLoading(true);
        setError(null);
        setResult(null);
        setAnalysis(null);

        try {
            const formData = new FormData();
            formData.append('own_products_file', ownProductsFile);
            formData.append('competitor_file', competitorFile);

            if (negativeFile) {
                formData.append('negative_file', negativeFile);
            }

            // Add filter configuration
            formData.append('filters', JSON.stringify(filters));

            const response = await axios.post(`${API_URL}/sp-product-campaigns/generate`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success) {
                const generatedCampaigns = response.data.campaigns || [];

                // Agregar campo 'selected' a cada campaña
                const campaignsWithSelection = generatedCampaigns.map(campaign => ({
                    ...campaign,
                    selected: true
                }));

                setCampaigns(campaignsWithSelection);
                setAnalysis(response.data.analysis);

                setResult({
                    success: true,
                    message: `${generatedCampaigns.length} campañas generadas. Selecciona cuáles crear en Amazon.`,
                    count: generatedCampaigns.length
                });
            } else {
                setError(response.data.error || 'Error al generar campañas');
            }
        } catch (err) {
            console.error('Error:', err);
            setError(err.response?.data?.error || err.message || 'Error al generar campañas');
        } finally {
            setLoading(false);
        }
    };

    // Crear campañas en Amazon
    const handleCreateInAmazon = async () => {
        const selectedCampaigns = campaigns.filter(c => c.selected);

        if (selectedCampaigns.length === 0) {
            setError('Debes seleccionar al menos una campaña para crear en Amazon');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const profileId = localStorage.getItem('selectedAccountValue');

            if (!profileId) {
                setError('Por favor selecciona una cuenta en el selector de arriba antes de crear campañas');
                setLoading(false);
                return;
            }

            const formData = new FormData();
            formData.append('own_products_file', ownProductsFile);
            formData.append('competitor_file', competitorFile);
            formData.append('profileId', profileId);
            formData.append('filters', JSON.stringify(filters));

            if (negativeFile) {
                formData.append('negative_file', negativeFile);
            }

            const selectedIndices = campaigns
                .map((c, idx) => c.selected ? idx : -1)
                .filter(idx => idx !== -1);
            formData.append('selected_campaign_indices', JSON.stringify(selectedIndices));
            formData.append('campaignState', campaignState);

            const response = await axios.post(`${API_URL}/sp-product-campaigns/create-in-amazon`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success) {
                const amazonResults = response.data.results || [];
                const errors = response.data.errors || [];

                setResult({
                    success: true,
                    message: response.data.message,
                    count: response.data.campaigns_created,
                    amazonResults: amazonResults,
                    errors: errors
                });

                const campaignsForTable = amazonResults.map(result => ({
                    'Campaign Name': result.campaignName,
                    'Campaign ID': result.campaignId,
                    'Ad Group ID': result.adGroupId,
                    'Product Ads': result.productAdsCount,
                    'Target ASINs': result.targetASINsCount,
                    'Status': 'Created in Amazon'
                }));

                setCampaigns(campaignsForTable);
                setCreationComplete(true);
            } else {
                setError(response.data.error || 'Error al crear campañas en Amazon');
            }
        } catch (err) {
            console.error('Error:', err);
            setError(err.response?.data?.error || err.message || 'Error al crear campañas en Amazon');
        } finally {
            setLoading(false);
        }
    };

    // Toggle selección
    const toggleCampaignSelection = (index) => {
        const updatedCampaigns = [...campaigns];
        updatedCampaigns[index].selected = !updatedCampaigns[index].selected;
        setCampaigns(updatedCampaigns);
    };

    const selectAllCampaigns = () => {
        const updatedCampaigns = campaigns.map(c => ({ ...c, selected: true }));
        setCampaigns(updatedCampaigns);
    };

    const deselectAllCampaigns = () => {
        const updatedCampaigns = campaigns.map(c => ({ ...c, selected: false }));
        setCampaigns(updatedCampaigns);
    };

    // Aplicar acción masiva
    const applyBulkAction = () => {
        if (!bulkAction) {
            setError('Selecciona una acción masiva');
            return;
        }

        const selectedCount = campaigns.filter(c => c.selected).length;
        if (selectedCount === 0) {
            setError('Selecciona al menos una campaña');
            return;
        }

        const updatedCampaigns = campaigns.map(campaign => {
            if (!campaign.selected) return campaign;

            const updated = { ...campaign };

            switch (bulkAction) {
                case 'budget':
                    if (bulkBudget && parseFloat(bulkBudget) > 0) {
                        updated.Budget = parseFloat(bulkBudget);
                    }
                    break;
                case 'startDate':
                    if (bulkStartDate) {
                        updated['Start Date'] = bulkStartDate;
                    }
                    break;
                case 'endDate':
                    if (bulkEndDate) {
                        updated['End Date'] = bulkEndDate;
                    }
                    break;
                default:
                    break;
            }

            return updated;
        });

        setCampaigns(updatedCampaigns);
        setBulkAction('');
        setBulkBudget('');
        setBulkStartDate('');
        setBulkEndDate('');
        setError(null);
        setResult({
            success: true,
            message: `Acción aplicada a ${selectedCount} campaña(s)`,
            count: selectedCount
        });
    };

    // Descargar CSV
    const handleDownloadCampaigns = () => {
        if (campaigns.length === 0) return;

        const headers = Object.keys(campaigns[0]);
        const csvContent = [
            headers.join(','),
            ...campaigns.map(campaign =>
                headers.map(header => {
                    const value = campaign[header];
                    if (typeof value === 'string' && value.includes(',')) {
                        return `"${value}"`;
                    }
                    return value;
                }).join(',')
            )
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `sp_product_campaigns_${Date.now()}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Reiniciar
    const handleReset = () => {
        setOwnProductsFile(null);
        setCompetitorFile(null);
        setNegativeFile(null);
        setCampaigns([]);
        setResult(null);
        setError(null);
        setAnalysis(null);
        setCreationComplete(false);
    };

    return (
        <Layout>
            <Container fluid className="sp-product-campaigns-page py-4">
                <Row className="mb-4">
                    <Col>
                        <h2 className="text-white mb-3">
                            <FaRocket className="me-2" />
                            Sponsored Products - Product Campaigns
                        </h2>
                        <p className="text-muted">
                            Genera campañas SP que targetean productos de la competencia con tus ASINs
                        </p>
                    </Col>
                </Row>

                {error && (
                    <Alert variant="danger" onClose={() => setError(null)} dismissible>
                        <FaExclamationTriangle className="me-2" />
                        {error}
                    </Alert>
                )}

                {/* Upload Section */}
                {!result && (
                    <>
                        {/* Upload Grid */}
                        <div className="upload-grid">
                            {/* Card 1: Own Products */}
                            <div className={`upload-card-new ${ownProductsFile ? 'has-file' : ''}`}>
                                <div className="card-required-badge">Requerido</div>
                                <div className="upload-icon"><FaFileAlt /></div>
                                <div className="upload-card-title">Productos Propios CSV</div>
                                <div className="upload-card-desc">
                                    Columnas: ASIN, SKU. Opcional: Price, Rating, Reviews, Abbreviation
                                </div>
                                <label className="w-100 mt-auto">
                                    <div className="upload-btn">
                                        {ownProductsFile ? 'Cambiar Archivo' : 'Seleccionar Archivo'}
                                    </div>
                                    <input type="file" accept=".csv" onChange={handleOwnProductsFileChange} hidden />
                                </label>
                                {ownProductsFile && <small className="text-success mt-2">{ownProductsFile.name}</small>}
                            </div>

                            {/* Card 2: Competitors */}
                            <div className={`upload-card-new ${competitorFile ? 'has-file' : ''}`}>
                                <div className="card-required-badge">Requerido</div>
                                <div className="upload-icon"><FaFileAlt /></div>
                                <div className="upload-card-title">Competidores CSV</div>
                                <div className="upload-card-desc">
                                    Columnas: ASIN. Opcional: Price, Rating, Reviews
                                </div>
                                <label className="w-100 mt-auto">
                                    <div className="upload-btn">
                                        {competitorFile ? 'Cambiar Archivo' : 'Seleccionar Archivo'}
                                    </div>
                                    <input type="file" accept=".csv" onChange={handleCompetitorFileChange} hidden />
                                </label>
                                {competitorFile && <small className="text-success mt-2">{competitorFile.name}</small>}
                            </div>

                            {/* Card 3: Negative Targeting */}
                            <div className={`upload-card-new ${negativeFile ? 'has-file' : ''}`}>
                                <div className="upload-icon"><FaFileAlt /></div>
                                <div className="upload-card-title">Negative Targeting (Opcional)</div>
                                <div className="upload-card-desc">
                                    Columnas: Abbreviation, Negative Targeting, Match Type
                                </div>
                                <label className="w-100 mt-auto">
                                    <div className="upload-btn">
                                        {negativeFile ? 'Cambiar Archivo' : 'Seleccionar Archivo'}
                                    </div>
                                    <input type="file" accept=".csv" onChange={handleNegativeFileChange} hidden />
                                </label>
                                {negativeFile && <small className="text-success mt-2">{negativeFile.name}</small>}
                            </div>
                        </div>

                        {/* Filters Section */}
                        <div className="config-section-card">
                            <h5 className="mb-3">
                                <FaFilter className="me-2 text-primary" />
                                Filtros Competitivos (Opcional)
                            </h5>
                            <p className="text-muted small mb-3">
                                Filtra los competidores para targetear solo aquellos donde tienes ventaja competitiva
                            </p>
                            <Row>
                                <Col md={4}>
                                    <Form.Check
                                        type="checkbox"
                                        id="filter-reviews"
                                        label="Por Reviews: Menos reviews que nosotros"
                                        checked={filters.by_reviews}
                                        onChange={() => handleFilterChange('by_reviews')}
                                        className="mb-2"
                                    />
                                </Col>
                                <Col md={4}>
                                    <Form.Check
                                        type="checkbox"
                                        id="filter-price"
                                        label="Por Precio: Mayor precio que nosotros"
                                        checked={filters.by_price}
                                        onChange={() => handleFilterChange('by_price')}
                                        className="mb-2"
                                    />
                                </Col>
                                <Col md={4}>
                                    <Form.Check
                                        type="checkbox"
                                        id="filter-rating"
                                        label="Por Rating: Menor rating que nosotros"
                                        checked={filters.by_rating}
                                        onChange={() => handleFilterChange('by_rating')}
                                        className="mb-2"
                                    />
                                </Col>
                            </Row>
                        </div>

                        {/* Generate Button */}
                        <div className="generate-section">
                            <button
                                className={`btn-generate-campaigns ${(!loading && ownProductsFile && competitorFile) ? 'ready' : ''}`}
                                onClick={handleGenerateCampaigns}
                                disabled={loading || !ownProductsFile || !competitorFile}
                            >
                                {loading ? (
                                    <>
                                        <Spinner size="sm" animation="border" className="me-2" />
                                        Generando campañas...
                                    </>
                                ) : (
                                    <>
                                        <FaRocket />
                                        Generar Campañas
                                    </>
                                )}
                            </button>
                        </div>
                    </>
                )}

                {/* Analysis Section */}
                {analysis && (
                    <Alert variant="info" className="mb-3">
                        <strong>📊 Análisis:</strong> {analysis.own_products_count} productos propios | {analysis.filtered_competitors_count} competidores filtrados |
                        Precio ratio: {analysis.price_ratio} | Bid base: ${analysis.base_bid} | {analysis.target_asins_per_campaign} ASINs target por campaña
                    </Alert>
                )}

                {/* Results Section */}
                {result && campaigns.length > 0 && (
                    <Row>
                        <Col>
                            <Card className="config-section-card border-0 p-0 overflow-hidden">
                                <Card.Header className="bg-dark text-white p-3">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5><FaRocket className="me-2" />Campañas Generadas ({campaigns.length})</h5>
                                    </div>

                                    <div>
                                        {/* Bulk Actions Row */}
                                        <div className="mb-3 d-flex justify-content-between align-items-center">
                                            <div className="d-flex gap-2 align-items-center">
                                                <span className="text-white fw-bold">Bulk actions:</span>
                                                <Form.Select
                                                    size="sm"
                                                    value={bulkAction}
                                                    onChange={(e) => setBulkAction(e.target.value)}
                                                    style={{ width: '200px' }}
                                                >
                                                    <option value="">Seleccionar acción</option>
                                                    <option value="budget">Asignar presupuesto...</option>
                                                    <option value="startDate">Asignar Start Date...</option>
                                                    <option value="endDate">Asignar End Date...</option>
                                                </Form.Select>

                                                {bulkAction === 'budget' && (
                                                    <Form.Control
                                                        type="number"
                                                        size="sm"
                                                        placeholder="Nuevo presupuesto"
                                                        value={bulkBudget}
                                                        onChange={(e) => setBulkBudget(e.target.value)}
                                                        style={{ width: '150px' }}
                                                        min="0"
                                                        step="0.01"
                                                    />
                                                )}

                                                {bulkAction === 'startDate' && (
                                                    <Form.Control
                                                        type="date"
                                                        size="sm"
                                                        value={bulkStartDate}
                                                        onChange={(e) => setBulkStartDate(e.target.value)}
                                                        style={{ width: '150px' }}
                                                    />
                                                )}

                                                {bulkAction === 'endDate' && (
                                                    <Form.Control
                                                        type="date"
                                                        size="sm"
                                                        value={bulkEndDate}
                                                        onChange={(e) => setBulkEndDate(e.target.value)}
                                                        style={{ width: '150px' }}
                                                    />
                                                )}

                                                {bulkAction && (
                                                    <Button
                                                        variant="warning"
                                                        size="sm"
                                                        onClick={applyBulkAction}
                                                    >
                                                        Aplicar
                                                    </Button>
                                                )}
                                            </div>
                                        </div>

                                        {/* Action Buttons Row */}
                                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                            <div className="d-flex gap-2 align-items-center">
                                                <Form.Select
                                                    size="sm"
                                                    value={campaignState}
                                                    onChange={(e) => setCampaignState(e.target.value)}
                                                    style={{ width: 'auto' }}
                                                    disabled={creationComplete}
                                                >
                                                    <option value="ENABLED">Crear como: Enabled</option>
                                                    <option value="PAUSED">Crear como: Paused</option>
                                                </Form.Select>

                                                <Button variant="outline-success" size="sm" onClick={handleDownloadCampaigns}>
                                                    <FaDownload className="me-1" /> Descargar CSV
                                                </Button>
                                                <Button variant="outline-primary" size="sm" onClick={handleReset}>
                                                    <FaRocket className="me-1" /> Nueva Generación
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-0 table-responsive bg-dark" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                                    <Table hover variant="dark" className="align-middle mb-0" size="sm" style={{ fontSize: '0.85rem' }}>
                                        <thead className="text-warning sticky-top bg-dark">
                                            <tr>
                                                <th width="40">
                                                    <Form.Check
                                                        type="checkbox"
                                                        checked={campaigns.every(c => c.selected)}
                                                        onChange={(e) => e.target.checked ? selectAllCampaigns() : deselectAllCampaigns()}
                                                    />
                                                </th>
                                                <th width="50">#</th>
                                                <th width="250">Campaign Name</th>
                                                <th width="100">ASIN</th>
                                                <th width="80">Bid</th>
                                                <th width="80">Budget</th>
                                                <th width="80">Targets</th>
                                                <th width="100">Price Ratio</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {campaigns.map((campaign, idx) => (
                                                <tr key={idx}>
                                                    <td>
                                                        <Form.Check
                                                            type="checkbox"
                                                            checked={campaign.selected || false}
                                                            onChange={() => toggleCampaignSelection(idx)}
                                                        />
                                                    </td>
                                                    <td>{idx + 1}</td>
                                                    <td>
                                                        <div className="text-truncate" style={{ maxWidth: '230px' }}>
                                                            {campaign['Campaign Name']}
                                                        </div>
                                                    </td>
                                                    <td className="text-info">{campaign.ASINs || campaign.ASIN}</td>
                                                    <td>${campaign.Bid}</td>
                                                    <td>${campaign.Budget}</td>
                                                    <td>
                                                        <Badge bg="primary">{campaign['Target Count'] || 0} ASINs</Badge>
                                                    </td>
                                                    <td>
                                                        <Badge bg={campaign['Price Ratio'] < 1 ? 'success' : campaign['Price Ratio'] > 1 ? 'warning' : 'secondary'}>
                                                            {campaign['Price Ratio']}x
                                                        </Badge>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                                <Card.Footer className="bg-dark border-top border-secondary d-flex justify-content-between p-3">
                                    <div className="d-flex gap-2">
                                        <Button variant="secondary" onClick={handleReset} className="fw-bold">← Cancelar</Button>
                                    </div>
                                    <div className="d-flex gap-3 align-items-center">
                                        <div className="text-muted small">
                                            Total: {campaigns.length} | Seleccionadas: {campaigns.filter(c => c.selected).length}
                                        </div>
                                        <Button
                                            variant="success"
                                            size="sm"
                                            onClick={handleCreateInAmazon}
                                            disabled={loading || creationComplete || campaigns.filter(c => c.selected).length === 0}
                                            style={{
                                                fontWeight: 'bold',
                                                padding: '6px 20px'
                                            }}
                                        >
                                            {loading ? (
                                                <>
                                                    <Spinner animation="border" size="sm" className="me-1" />
                                                    Creando en Amazon...
                                                </>
                                            ) : creationComplete ? (
                                                <>
                                                    <FaCheckCircle className="me-1" /> Campañas Creadas
                                                </>
                                            ) : (
                                                <>
                                                    Crear Campañas Seleccionadas
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </Card.Footer>
                            </Card>
                        </Col>
                    </Row>
                )}

                {/* Requirements Section */}
                {!result && (
                    <div className="requirements-section">
                        <span className="requirements-title">📋 Requisitos de los archivos CSV</span>
                        <div className="req-item"><strong>Productos Propios CSV:</strong> Debe contener 'ASIN', 'SKU'. Opcional: Price, Rating, Reviews, Abbreviation.</div>
                        <div className="req-item"><strong>Competidores CSV:</strong> Debe contener 'ASIN'. Opcional: Price, Rating, Reviews para filtros.</div>
                        <div className="req-item"><strong>Negative Targeting CSV:</strong> Debe contener 'Abbreviation', 'Negative Targeting', 'Match Type'.</div>
                    </div>
                )}
            </Container>
        </Layout>
    );
};

export default SpProductCampaigns;
